/**
 * Hook to get the current user's permission level for a document.
 * Returns 'owner' if user owns the document, or their share permission level.
 */

import { useState, useEffect } from 'react'
import { useAuthStore } from '@stores/authStore'
import { documentSharesAPI } from '@api/documentShares'
import { logError } from '@utils/logger'
import type { Document, PermissionLevel } from '@/types'

export function useDocumentPermission(document: Document | null): {
  permission: PermissionLevel | null
  isLoading: boolean
  canEdit: boolean
  canComment: boolean
} {
  const { user } = useAuthStore()
  const [permission, setPermission] = useState<PermissionLevel | null>(null)
  const [isLoading, setIsLoading] = useState(true)

  useEffect(() => {
    const fetchPermission = async () => {
      if (!document || !user) {
        setPermission(null)
        setIsLoading(false)
        return
      }

      // Check if user is the owner FIRST - owner always has full permissions
      // This check must come before share check to prevent self-shares from overriding owner status
      if (document.owner_id === user.id) {
        setPermission('owner')
        setIsLoading(false)
        return
      }

      // Only check shares if user is NOT the owner
      // Check if user has a share for this document
      try {
        const shares = await documentSharesAPI.getDocumentShares(document.id)
        const userShare = shares.find(
          (share) => share.shared_with_user_id === user.id
        )

        if (userShare) {
          // Double-check: if somehow a self-share exists, ignore it and use owner
          if (userShare.shared_with_user_id === document.owner_id) {
            setPermission('owner')
          } else {
            setPermission(userShare.permission_level)
          }
        } else {
          // No share found - user shouldn't have access, but set to viewer as fallback
          setPermission('viewer')
        }
      } catch (error) {
        logError('Failed to fetch document permission:', error)
        // Default to viewer on error
        setPermission('viewer')
      } finally {
        setIsLoading(false)
      }
    }

    fetchPermission()
  }, [document, user])

  const canEdit = permission === 'owner' || permission === 'editor'
  const canComment = permission === 'owner' || permission === 'editor' || permission === 'reviewer'

  return {
    permission,
    isLoading,
    canEdit,
    canComment,
  }
}

